import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  InputAdornment,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  Collapse,
  Skeleton,
} from '@mui/material';
import {
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Edit as EditIcon,
  Cancel as CancelIcon,
  LibraryAdd as LibraryAddIcon,
  Clear as ClearIcon,
  ArrowForward as ArrowForwardIcon,
  Add as AddIcon,
  AssignmentInd as AssignmentIndIcon,
} from '@mui/icons-material';
import {
  GlobalLeague,
  GlobalLeagueDivision,
  globalLeagueCatalogService,
} from '@/services/globalLeagueService';
import { useQuery } from 'react-query';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import CustomLeagueFormContent from './CustomLeagueFormContent';
import { LeagueFormData, DEFAULT_FORM_DATA, DiscoveryPlatform, detectDiscoveryPlatform } from './LeagueFormDialog';
import dayjs, { Dayjs } from 'dayjs';

// Data passed when user selects a division from catalog
export interface CatalogDivisionSelection {
  division: GlobalLeagueDivision;
  globalLeague: GlobalLeague;
  teams: string[]; // Pre-fetched team names from preview
  selectedTeamName?: string; // Team selected via team picker mode
  selectedPlayerId?: string; // Player to assign (from picker or pre-selected)
  selectedPlayerName?: string; // Player name for display
}

type ViewState = 'catalog' | 'division' | 'custom';

interface LeagueCatalogDialogProps {
  open: boolean;
  onClose: () => void;
  onImport: (league: any, opts?: { teamName?: string; playerId?: string }) => void;
  onCustomLeague?: () => void;
  onSelectDivision?: (selection: CatalogDivisionSelection) => void; // New: pass division to form
  initialLeague?: GlobalLeague | null; // Resume at division view with this league selected
  onBack?: () => void; // Go back to previous dialog (e.g. Add Team)
  teamPickerMode?: boolean; // Enable team selection on pills
  players?: { id: string; name: string }[]; // List of org players (for player picker when player unknown)
  selectedPlayerId?: string; // Pre-selected player (from PlayerTeamsTab where player is already known)
  selectedPlayerName?: string; // Player name for display
  // Custom league inline support
  onCustomLeagueSubmit?: (formData: LeagueFormData, discoveryPlatform: DiscoveryPlatform) => void;
  onDiscoveryRequest?: (formData: LeagueFormData, platform: DiscoveryPlatform) => void;
  user?: any;
  orgSettings?: any;
}

const LeagueCatalogDialog: React.FC<LeagueCatalogDialogProps> = ({ open, onClose, onImport, onCustomLeague, onSelectDivision, initialLeague, onBack, teamPickerMode, players, selectedPlayerId, selectedPlayerName, onCustomLeagueSubmit, onDiscoveryRequest, user, orgSettings }) => {
  // View state
  const [currentView, setCurrentView] = useState<ViewState>('catalog');
  const catalogScrollRef = useRef<HTMLDivElement>(null);
  const catalogScrollPos = useRef(0);

  // League selection
  const [leagueSearch, setLeagueSearch] = useState('');
  const debouncedLeagueSearch = useDebouncedValue(leagueSearch, 300);
  const [selectedLeague, setSelectedLeague] = useState<GlobalLeague | null>(null);

  // Division selection - store all divisions, filter client-side
  const [allDivisions, setAllDivisions] = useState<GlobalLeagueDivision[]>([]);
  const [loadingDivisions, setLoadingDivisions] = useState(false);
  const [selectedDivision, setSelectedDivision] = useState<GlobalLeagueDivision | null>(null);
  const teamSearchRef = useRef('');
  const teamSearchInputRef = useRef<HTMLInputElement>(null);
  const teamSearchDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const [debouncedTeamSearch, setDebouncedTeamSearch] = useState('');
  const [ageGroupFilter, setAgeGroupFilter] = useState<string>('');
  const [genderFilter, setGenderFilter] = useState<string>('');
  const [subLeagueFilter, setSubLeagueFilter] = useState<string>('');

  const handleTeamSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    teamSearchRef.current = e.target.value;
    if (teamSearchDebounceRef.current) clearTimeout(teamSearchDebounceRef.current);
    teamSearchDebounceRef.current = setTimeout(() => {
      setDebouncedTeamSearch(teamSearchRef.current);
    }, 300);
  }, []);

  const clearTeamSearch = useCallback(() => {
    teamSearchRef.current = '';
    if (teamSearchInputRef.current) teamSearchInputRef.current.value = '';
    if (teamSearchDebounceRef.current) clearTimeout(teamSearchDebounceRef.current);
    setDebouncedTeamSearch('');
  }, []);

  // Team search query key
  const teamSearchQuery = debouncedTeamSearch.trim();
  const teamSearchEnabled = open && currentView === 'division' && !!selectedLeague && teamSearchQuery.length >= 2;

  // Delayed loading indicator for team search (avoids flash on fast/cached results)
  const [showTeamSearchLoading, setShowTeamSearchLoading] = useState(false);

  // Team preview
  const [showTeamPreview, setShowTeamPreview] = useState(false);
  const [teamPreview, setTeamPreview] = useState<string[]>([]);
  const [loadingTeams, setLoadingTeams] = useState(false);

  // Team picker state
  const [selectedTeamName, setSelectedTeamName] = useState<string | null>(null);
  const [selectedTeamDivisionId, setSelectedTeamDivisionId] = useState<string | null>(null);
  const [selectedPlayerIdLocal, setSelectedPlayerIdLocal] = useState<string | null>(null);

  // Import state
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Already imported divisions
  const [importedDivisionIds, setImportedDivisionIds] = useState<Set<string>>(new Set());

  // Custom form state (View 3)
  const [customFormData, setCustomFormData] = useState<LeagueFormData>(DEFAULT_FORM_DATA);
  const [customFieldErrors, setCustomFieldErrors] = useState<Record<string, string>>({});
  const [customSubmitting, setCustomSubmitting] = useState(false);
  const [customSyncTime, setCustomSyncTime] = useState<Dayjs | null>(dayjs().hour(6).minute(0));
  const [customDiscoveryPlatform, setCustomDiscoveryPlatform] = useState<DiscoveryPlatform>(null);

  // Fetch catalog leagues with react-query (enables prefetching from dashboard)
  const { data: leagues = [], isLoading: loadingLeagues } = useQuery(
    ['league-catalog', debouncedLeagueSearch || ''],
    () => globalLeagueCatalogService.getCatalog(debouncedLeagueSearch || undefined),
    {
      enabled: open && currentView === 'catalog',
      staleTime: 5 * 60 * 1000,
      keepPreviousData: true,
    }
  );

  // Team search with react-query (keepPreviousData prevents layout thrashing)
  const {
    data: teamSearchData,
    isLoading: isLoadingTeamSearch,
  } = useQuery(
    ['team-search', selectedLeague?.id, teamSearchQuery],
    () => globalLeagueCatalogService.searchTeams(selectedLeague!.id, teamSearchQuery),
    {
      enabled: teamSearchEnabled,
      staleTime: 30 * 1000,
      keepPreviousData: true,
      // Re-poll while backend is still caching teams
      refetchInterval: (data) => data?.caching ? 3000 : false,
    }
  );

  // Delay showing team search loading to avoid flash on cached/fast results
  useEffect(() => {
    if (isLoadingTeamSearch && teamSearchEnabled) {
      const timer = setTimeout(() => setShowTeamSearchLoading(true), 150);
      return () => clearTimeout(timer);
    } else {
      setShowTeamSearchLoading(false);
    }
  }, [isLoadingTeamSearch, teamSearchEnabled]);

  // Delayed skeleton display to avoid flash on fast loads
  const [showLeagueSkeleton, setShowLeagueSkeleton] = useState(false);
  const [showDivisionSkeleton, setShowDivisionSkeleton] = useState(false);

  // Content ready state - prevents content from flashing in
  const [leagueContentReady, setLeagueContentReady] = useState(false);
  const [divisionContentReady, setDivisionContentReady] = useState(false);
  const [dialogReady, setDialogReady] = useState(false);

  // Wait for dialog to be ready before showing any content
  useEffect(() => {
    if (open) {
      setDialogReady(false);
      const timer = setTimeout(() => setDialogReady(true), 50);
      return () => clearTimeout(timer);
    } else {
      setDialogReady(false);
    }
  }, [open]);

  // Delay skeleton display and track content ready state
  useEffect(() => {
    if (loadingLeagues) {
      setLeagueContentReady(false);
      const timer = setTimeout(() => setShowLeagueSkeleton(true), 150);
      return () => clearTimeout(timer);
    } else {
      setShowLeagueSkeleton(false);
      const timer = setTimeout(() => setLeagueContentReady(true), 10);
      return () => clearTimeout(timer);
    }
  }, [loadingLeagues, open]);

  useEffect(() => {
    if (loadingDivisions) {
      setDivisionContentReady(false);
      const timer = setTimeout(() => setShowDivisionSkeleton(true), 150);
      return () => clearTimeout(timer);
    } else {
      setShowDivisionSkeleton(false);
      const timer = setTimeout(() => setDivisionContentReady(true), 10);
      return () => clearTimeout(timer);
    }
  }, [loadingDivisions, open]);

  // Reset state when dialog closes so it's ready for next open
  useEffect(() => {
    if (!open) {
      if (!initialLeague) {
        setCurrentView('catalog');
        setSelectedLeague(null);
        setSelectedDivision(null);
        setLeagueSearch('');
        clearTeamSearch();
        setAgeGroupFilter('');
        setGenderFilter('');
        setSubLeagueFilter('');
        setAllDivisions([]);
        setShowTeamPreview(false);
        setTeamPreview([]);
        setSelectedTeamName(null);
        setSelectedTeamDivisionId(null);
        setSelectedPlayerIdLocal(null);
        setCustomFormData(DEFAULT_FORM_DATA);
        setCustomFieldErrors({});
        setCustomSubmitting(false);
        setCustomSyncTime(dayjs().hour(6).minute(0));
        setCustomDiscoveryPlatform(null);

        setError(null);
        setImportedDivisionIds(new Set());
      }
      setLeagueContentReady(false);
      setDivisionContentReady(false);
      setDialogReady(false);
      catalogScrollPos.current = 0;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialLeague]);

  // Load imported divisions for the current org
  const loadImportedDivisions = async () => {
    try {
      const ids = await globalLeagueCatalogService.getImportedDivisions();
      setImportedDivisionIds(new Set(ids));
    } catch (err) {
      console.error('Failed to load imported divisions:', err);
    }
  };

  // Load imported divisions when dialog opens
  useEffect(() => {
    if (open) {
      loadImportedDivisions();

      if (initialLeague) {
        setCurrentView('division');
        setSelectedLeague(initialLeague);
        setSelectedDivision(null);
        loadDivisions(initialLeague);
        globalLeagueCatalogService.warmTeamCache(initialLeague.id).catch(() => {});
      }
    }
  }, [open, initialLeague]);


  // Load divisions for a league
  const loadDivisions = async (league: GlobalLeague) => {
    if (league.divisions?.length) {
      setAllDivisions(league.divisions);
      return;
    }
    setLoadingDivisions(true);
    setError(null);
    try {
      const data = await globalLeagueCatalogService.getDivisions(league.id);
      setAllDivisions(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load divisions');
    } finally {
      setLoadingDivisions(false);
    }
  };

  // Simple view switching — saves scroll position when leaving catalog
  const switchView = useCallback((targetView: ViewState) => {
    if (currentView === 'catalog' && catalogScrollRef.current) {
      catalogScrollPos.current = catalogScrollRef.current.scrollTop;
    }
    setCurrentView(targetView);
  }, [currentView]);

  // Handle league selection
  const handleLeagueSelect = (league: GlobalLeague) => {
    setSelectedLeague(league);
    setSelectedDivision(null);
    clearTeamSearch();
    setAgeGroupFilter('');
    setGenderFilter('');
    setSubLeagueFilter('');
    switchView('division');
    loadDivisions(league);
    globalLeagueCatalogService.warmTeamCache(league.id).catch(() => {});
  };

  // Handle custom league card click
  const handleCustomLeagueClick = () => {
    if (onCustomLeague && !onCustomLeagueSubmit) {
      onCustomLeague();
      return;
    }
    setCustomFormData(DEFAULT_FORM_DATA);
    setCustomFieldErrors({});
    setCustomSyncTime(dayjs().hour(6).minute(0));
    setCustomDiscoveryPlatform(null);
    switchView('custom');
  };

  // Sub-league display label mapping
  const subLeagueLabel = (value: string): string => {
    switch (value) {
      case 'ecnl': return 'ECNL';
      case 'ecnlrl': return 'ECNL Regional League';
      default: return value;
    }
  };

  // Get unique filter options from all divisions
  const filterOptions = useMemo(() => {
    const ageGroups = [...new Set(allDivisions.map((d) => d.ageGroup).filter(Boolean))] as string[];
    const genders = [...new Set(allDivisions.map((d) => d.gender).filter(Boolean))] as string[];
    const subLeagues = [...new Set(allDivisions.map((d) => d.subLeague).filter(Boolean))] as string[];
    return {
      ageGroups: ageGroups.sort(),
      genders: genders.sort(),
      subLeagues: subLeagues.sort(),
    };
  }, [allDivisions]);

  // Sort divisions by age prefix then tier
  const sortDivisions = useCallback(<T extends { name: string }>(items: T[]): T[] => {
    const tierPatterns: [RegExp, number][] = [
      [/\belite\b/i, 0],
      [/\bpremier[e]?\b/i, 1],
      [/\b1st\b/i, 2],
      [/\b2nd\b/i, 3],
      [/\b3rd\b/i, 4],
    ];
    const getTierRank = (name: string): number => {
      for (const [pattern, rank] of tierPatterns) {
        if (pattern.test(name)) return rank;
      }
      return 5;
    };
    return [...items].sort((a, b) => {
      const prefixA = a.name.match(/^(\d+(?:[_]\d+)?[A-Za-z]{0,3})\s/)?.[1] || '';
      const prefixB = b.name.match(/^(\d+(?:[_]\d+)?[A-Za-z]{0,3})\s/)?.[1] || '';
      if (prefixA !== prefixB) return prefixA.localeCompare(prefixB, undefined, { numeric: true });
      const rankDiff = getTierRank(a.name) - getTierRank(b.name);
      if (rankDiff !== 0) return rankDiff;
      return a.name.localeCompare(b.name);
    });
  }, []);

  // Filter team search results by active filters
  const filteredTeamSearchDivisions = useMemo(() => {
    if (!teamSearchData?.divisions) return [];
    const filtered = teamSearchData.divisions.filter(({ division }) => {
      if (ageGroupFilter && division.ageGroup !== ageGroupFilter) return false;
      if (genderFilter && division.gender !== genderFilter) return false;
      if (subLeagueFilter && division.subLeague !== subLeagueFilter) return false;
      return true;
    });
    return sortDivisions(filtered.map(d => ({ ...d, name: d.division.name }))).map(sorted =>
      filtered.find(f => f.division.name === sorted.name)!
    );
  }, [teamSearchData, ageGroupFilter, genderFilter, subLeagueFilter, sortDivisions]);

  // Filter divisions client-side
  const filteredDivisions = useMemo(() => {
    const filtered = allDivisions.filter((division) => {
      if (ageGroupFilter && division.ageGroup !== ageGroupFilter) return false;
      if (genderFilter && division.gender !== genderFilter) return false;
      if (subLeagueFilter && division.subLeague !== subLeagueFilter) return false;
      return true;
    });
    return sortDivisions(filtered);
  }, [allDivisions, ageGroupFilter, genderFilter, subLeagueFilter, sortDivisions]);

  // Clear selection when filtered results change and selected item is no longer visible
  useEffect(() => {
    if (selectedDivision && !filteredDivisions.some(d => d.id === selectedDivision.id)) {
      setSelectedDivision(null);
      setShowTeamPreview(false);
    }
  }, [filteredDivisions, selectedDivision]);

  // Handle team preview
  const handleTeamPreview = async (division: GlobalLeagueDivision) => {
    if (selectedDivision?.id === division.id) {
      setSelectedDivision(null);
      setShowTeamPreview(false);
      setTeamPreview([]);
      setSelectedTeamName(null);
      setSelectedTeamDivisionId(null);
      return;
    }

    setSelectedDivision(division);
    setShowTeamPreview(true);
    setLoadingTeams(true);
    setTeamPreview([]);
    setSelectedTeamName(null);
    setSelectedTeamDivisionId(null);

    try {
      const result = await globalLeagueCatalogService.previewTeams(division.id);
      setTeamPreview(result.teams || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load teams');
    } finally {
      setLoadingTeams(false);
    }
  };

  // Handle selecting a division to configure in form
  const handleSelectDivision = () => {
    if (!selectedDivision || !selectedLeague) return;

    if (onSelectDivision) {
      const effectivePlayerId = selectedPlayerId || selectedPlayerIdLocal || undefined;
      const effectivePlayerName = effectivePlayerId && players
        ? players.find(p => p.id === effectivePlayerId)?.name
        : undefined;

      onSelectDivision({
        division: selectedDivision,
        globalLeague: selectedLeague,
        teams: teamPreview,
        selectedTeamName: selectedTeamName || undefined,
        selectedPlayerId: effectivePlayerId,
        selectedPlayerName: effectivePlayerName,
      });
    }
  };

  // Handle direct import
  const handleImport = async () => {
    if (!selectedDivision) return;

    setImporting(true);
    setError(null);

    try {
      const league = await globalLeagueCatalogService.importFromCatalog({
        divisionId: selectedDivision.id,
        teams: teamPreview,
      });
      const effectivePlayerId = selectedPlayerId || selectedPlayerIdLocal || undefined;
      onImport(league, selectedTeamName ? { teamName: selectedTeamName, playerId: effectivePlayerId } : undefined);
    } catch (err: any) {
      setError(err.message || 'Failed to import league');
    } finally {
      setImporting(false);
    }
  };

  // Go back to catalog view
  const handleBackToCatalog = () => {
    switchView('catalog');
    setSelectedLeague(null);
    setSelectedDivision(null);
    clearTeamSearch();
    setAgeGroupFilter('');
    setGenderFilter('');
    setSubLeagueFilter('');
    setAllDivisions([]);
    setShowTeamPreview(false);
    setSelectedTeamName(null);
    setSelectedPlayerIdLocal(null);
    requestAnimationFrame(() => {
      if (catalogScrollRef.current) {
        catalogScrollRef.current.scrollTop = catalogScrollPos.current;
      }
    });
  };

  // Go back from custom form to catalog
  const handleBackFromCustom = () => {
    switchView('catalog');
    setCustomFormData(DEFAULT_FORM_DATA);
    setCustomFieldErrors({});
    requestAnimationFrame(() => {
      if (catalogScrollRef.current) {
        catalogScrollRef.current.scrollTop = catalogScrollPos.current;
      }
    });
  };

  // Handle team chip click in team picker mode
  const handleTeamChipClick = (teamName: string, division?: GlobalLeagueDivision, knownTeams?: string[]) => {
    if (!teamPickerMode) return;
    const deselecting = selectedTeamName === teamName && selectedTeamDivisionId === division?.id;
    setSelectedTeamName(deselecting ? null : teamName);
    setSelectedTeamDivisionId(deselecting ? null : division?.id || null);

    if (!deselecting && division && selectedDivision?.id !== division.id) {
      setSelectedDivision(division);
      if (knownTeams && knownTeams.length > 0) {
        setTeamPreview(knownTeams);
        setLoadingTeams(false);
        setShowTeamPreview(false);
      } else {
        handleTeamPreview(division);
      }
    }
  };

  // Render a team chip (clickable in team picker mode)
  const renderTeamChip = (teamName: string, idx: number, variant: 'outlined' | 'filled' = 'outlined', division?: GlobalLeagueDivision, knownTeams?: string[]) => {
    if (teamPickerMode) {
      const isSelected = selectedTeamName === teamName && selectedTeamDivisionId === division?.id;
      return (
        <Chip
          key={idx}
          label={teamName}
          size="small"
          color={isSelected ? 'primary' : 'default'}
          variant={isSelected ? 'filled' : 'outlined'}
          onClick={(e) => {
            e.stopPropagation();
            handleTeamChipClick(teamName, division, knownTeams);
          }}
          sx={{ cursor: 'pointer' }}
        />
      );
    }
    return (
      <Chip key={idx} label={teamName} size="small" color={variant === 'outlined' ? undefined : 'primary'} variant={variant} />
    );
  };

  // "Assign" mode: only when a specific player is pre-selected (from PlayerTeamsTab)
  const isAssignMode = !!selectedPlayerId;

  // Button text logic
  const getActionButtonText = () => {
    if (onSelectDivision) return 'Next';
    if (isAssignMode) return 'Assign';
    return 'Add';
  };

  // Button icon logic
  const getActionButtonIcon = () => {
    if (importing) return <CircularProgress size={20} />;
    if (onSelectDivision) return <ArrowForwardIcon />;
    if (isAssignMode) return <AssignmentIndIcon />;
    return importing ? undefined : <AddIcon />;
  };

  // Custom form input change handler
  const handleCustomInputChange = (field: keyof LeagueFormData, value: any) => {
    setCustomFormData(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'website' && value && value.trim() !== '' && !prev.autoSyncEnabled) {
        updated.autoSyncEnabled = true;
      }
      if (field === 'website' && (!value || value.trim() === '') && prev.autoSyncEnabled) {
        updated.autoSyncEnabled = false;
      }
      return updated;
    });
    if (field === 'website') {
      setCustomDiscoveryPlatform(detectDiscoveryPlatform(value || ''));
    }
    if (customFieldErrors[field]) {
      setCustomFieldErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  // Custom form sync time change
  useEffect(() => {
    if (customSyncTime) {
      const timeString = customSyncTime.format('HH:mm:ss');
      setCustomFormData(prev => ({ ...prev, syncTimeOfDay: timeString }));
    }
  }, [customSyncTime]);

  // Custom form submit
  const handleCustomSubmit = () => {
    const errors: Record<string, string> = {};
    if (!customFormData.name.trim()) {
      errors.name = 'League name is required';
    }
    if (!customFormData.website || customFormData.website.trim() === '') {
      errors.website = 'Website is required for new leagues';
    }
    if (Object.keys(errors).length > 0) {
      setCustomFieldErrors(errors);
      return;
    }

    if (customDiscoveryPlatform && onDiscoveryRequest) {
      onDiscoveryRequest(customFormData, customDiscoveryPlatform);
    } else if (onCustomLeagueSubmit) {
      setCustomSubmitting(true);
      onCustomLeagueSubmit(customFormData, customDiscoveryPlatform);
    }
  };

  const customFormValid = customFormData.name.trim() !== '' && customFormData.website.trim() !== '';

  // Whether to show the player picker inline (leagues screen with teamPickerMode + players list, no pre-selected player)
  const showPlayerPicker = teamPickerMode && selectedTeamName && players && players.length > 0 && !selectedPlayerId;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth transitionDuration={0} hideBackdrop={!!onBack} disableAutoFocus>
      <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider' }}>
        {/* View 1: Catalog header */}
        {currentView === 'catalog' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LibraryAddIcon />
            Add League from Catalog
          </Box>
        )}
        {/* View 2: Division header showing selected league */}
        {currentView === 'division' && selectedLeague && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {selectedLeague.logoUrl && (
              <Box
                component="img"
                src={selectedLeague.logoUrl}
                alt={selectedLeague.name}
                sx={{
                  width: 24,
                  height: 24,
                  objectFit: 'contain',
                  borderRadius: 0.5,
                }}
              />
            )}
            <Typography variant="h6" component="span" noWrap sx={{ minWidth: 0 }}>
              {selectedLeague.name}
            </Typography>
          </Box>
        )}
        {/* View 3: Custom league header */}
        {currentView === 'custom' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EditIcon fontSize="small" />
            <Typography variant="h6" component="span">
              Custom League
            </Typography>
          </Box>
        )}
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', minHeight: 450, maxHeight: 450, p: 0, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
          {/* View 1: Search box (non-scrolling) */}
          {currentView === 'catalog' && (
            <Box sx={{ flexShrink: 0, px: 3, pt: 2, pb: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search leagues..."
                value={leagueSearch}
                onChange={(e) => setLeagueSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: leagueSearch && (
                    <InputAdornment position="end">
                      <Button size="small" onClick={() => setLeagueSearch('')} startIcon={<ClearIcon />}>
                        Clear
                      </Button>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          )}

          {/* View 2: League info + Search + Filters (non-scrolling) */}
          {currentView === 'division' && selectedLeague && (
            <Box sx={{ flexShrink: 0, px: 3, pt: 2, pb: 2 }}>
              {/* Search by team name */}
              <TextField
                fullWidth
                size="small"
                placeholder="Search by team name..."
                inputRef={teamSearchInputRef}
                onChange={handleTeamSearchChange}
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: debouncedTeamSearch && (
                    <InputAdornment position="end">
                      <Button size="small" onClick={clearTeamSearch} startIcon={<ClearIcon />}>
                        Clear
                      </Button>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Filters */}
              <Grid container spacing={2}>
                {filterOptions.subLeagues.length > 1 && (
                  <Grid item xs={12}>
                    <FormControl fullWidth size="small">
                      <InputLabel>League</InputLabel>
                      <Select
                        value={subLeagueFilter}
                        label="League"
                        onChange={(e) => setSubLeagueFilter(e.target.value)}
                      >
                        <MenuItem value="">All</MenuItem>
                        {filterOptions.subLeagues.map((sl) => (
                          <MenuItem key={sl} value={sl}>
                            {subLeagueLabel(sl)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Age Group</InputLabel>
                    <Select
                      value={ageGroupFilter}
                      label="Age Group"
                      onChange={(e) => setAgeGroupFilter(e.target.value)}
                    >
                      <MenuItem value="">All</MenuItem>
                      {filterOptions.ageGroups.map((ag) => (
                        <MenuItem key={ag} value={ag}>
                          {ag}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Gender</InputLabel>
                    <Select
                      value={genderFilter}
                      label="Gender"
                      onChange={(e) => setGenderFilter(e.target.value)}
                    >
                      <MenuItem value="">All</MenuItem>
                      {filterOptions.genders.map((g) => (
                        <MenuItem key={g} value={g}>
                          {g}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Scrollable content area */}
          <Box ref={currentView === 'catalog' ? catalogScrollRef : undefined} sx={{ flex: 1, overflow: 'auto', px: currentView === 'custom' ? 0 : 3, pb: 2 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2, mx: currentView === 'custom' ? 3 : 0 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {/* View 1: League Selection */}
            {currentView === 'catalog' && (
              <Box sx={{
                opacity: dialogReady && (leagueContentReady || showLeagueSkeleton) ? 1 : 0,
                transition: 'opacity 0.15s ease-in'
              }}>
                <Grid container spacing={2}>
                  {/* Custom League Option - always visible */}
                  {(onCustomLeague || onCustomLeagueSubmit) && (
                    <Grid item xs={12}>
                      <Card
                        variant="outlined"
                        sx={{
                          borderStyle: 'dashed',
                        }}
                      >
                        <CardActionArea onClick={handleCustomLeagueClick}>
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Box
                                sx={{
                                  width: 48,
                                  height: 48,
                                  borderRadius: 2,
                                  bgcolor: 'grey.200',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  mr: 2,
                                }}
                              >
                                <EditIcon sx={{ color: 'grey.600', fontSize: 28 }} />
                              </Box>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="subtitle1" fontWeight="medium">
                                  Custom League
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Add a league schedule
                                </Typography>
                              </Box>
                            </Box>
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    </Grid>
                  )}
                  {/* Loading skeletons */}
                  {showLeagueSkeleton ? (
                    <>
                      {[1, 2, 3, 4].map((i) => (
                        <Grid item xs={12} sm={6} key={i}>
                          <Card variant="outlined" sx={{ '& .MuiSkeleton-root': { animationDelay: '0s !important' } }}>
                            <CardContent>
                              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                <Skeleton animation="wave" variant="rounded" width={40} height={40} sx={{ mr: 2, flexShrink: 0 }} />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Skeleton animation="wave" variant="text" width="70%" height={28} />
                                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                                    <Skeleton animation="wave" variant="rounded" width={65} height={24} />
                                    <Skeleton animation="wave" variant="rounded" width={85} height={24} />
                                  </Box>
                                </Box>
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </>
                  ) : loadingLeagues ? (
                    null
                  ) : leagues.length === 0 && leagueSearch ? (
                    <Grid item xs={12}>
                      <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                        No leagues found matching "{leagueSearch}"
                      </Typography>
                    </Grid>
                  ) : leagues.map((league) => (
                    <Grid item xs={12} sm={6} key={league.id}>
                      <Card variant="outlined">
                        <CardActionArea onClick={() => handleLeagueSelect(league)}>
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                              {league.logoUrl && (
                                <Box
                                  component="img"
                                  src={league.logoUrl}
                                  alt={league.name}
                                  sx={{
                                    width: 40,
                                    height: 40,
                                    objectFit: 'contain',
                                    mr: 2,
                                    borderRadius: 1,
                                  }}
                                  onError={(e: any) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                              )}
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="subtitle1" noWrap>
                                  {league.name}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                                  <Chip
                                    label={league.platformType === 'GOTSPORT' ? 'GotSport' : league.platformType === 'MODULAR11' ? 'Modular11' : league.platformType === 'TGS' ? 'TGS' : 'SincSports'}
                                    size="small"
                                    sx={
                                      league.platformType === 'MODULAR11' ? { bgcolor: 'grey.600', color: 'white' } :
                                      league.platformType === 'TGS' ? { bgcolor: '#7b1fa2', color: 'white' } :
                                      undefined
                                    }
                                    color={league.platformType === 'GOTSPORT' ? 'primary' : (league.platformType === 'MODULAR11' || league.platformType === 'TGS') ? undefined : 'info'}
                                  />
                                  <Chip
                                    label={`${league._count?.divisions || league.divisions?.length || 0} divisions`}
                                    size="small"
                                    variant="outlined"
                                  />
                                </Box>
                              </Box>
                            </Box>
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* View 2: Division Selection */}
            {currentView === 'division' && selectedLeague && (
              <Box sx={{
                opacity: divisionContentReady || showDivisionSkeleton ? 1 : 0,
                transition: 'opacity 0.15s ease-in'
              }}>
                {/* Team search results */}
                {teamSearchEnabled ? (
                  !teamSearchData ? (
                    showTeamSearchLoading ? (
                      <List sx={{ '& .MuiSkeleton-root': { animationDelay: '0s !important' } }}>
                        {[1, 2, 3].map((i) => (
                          <Box key={i} sx={{ px: 2, py: 1.5, mb: 0.5 }}>
                            <Skeleton animation="wave" variant="text" width="60%" height={24} sx={{ mb: 0.5 }} />
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Skeleton animation="wave" variant="rounded" width={100} height={24} />
                              <Skeleton animation="wave" variant="rounded" width={120} height={24} />
                            </Box>
                          </Box>
                        ))}
                      </List>
                    ) : null
                  ) :
                  <>
                    {/* Caching indicator */}
                    {teamSearchData.caching && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, mb: 0.5 }}>
                        <CircularProgress size={14} />
                        <Typography variant="body2" color="text.secondary">
                          Searched {teamSearchData.searchedDivisions} of {teamSearchData.totalDivisions} divisions...
                        </Typography>
                      </Box>
                    )}
                    {filteredTeamSearchDivisions.length === 0 && !teamSearchData.caching ? (
                    <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                      No teams found matching "{debouncedTeamSearch}"
                    </Typography>
                  ) : (
                    <List>
                      {filteredTeamSearchDivisions.map(({ division, matchingTeams }) => {
                        const isAlreadyImported = importedDivisionIds.has(division.id);
                        return (
                          <Box
                            key={division.id}
                            sx={{
                              borderRadius: 1,
                              mb: 0.5,
                              bgcolor: selectedDivision?.id === division.id ? 'primary.50' : 'transparent',
                              border: 2,
                              borderColor: selectedDivision?.id === division.id ? 'primary.main' : 'transparent',
                              opacity: isAlreadyImported ? 0.6 : 1,
                              '@media (hover: hover)': {
                                '&:hover': {
                                  bgcolor: isAlreadyImported
                                    ? 'transparent'
                                    : selectedDivision?.id === division.id
                                      ? 'primary.50'
                                      : 'action.hover'
                                },
                              },
                            }}
                          >
                            <Box
                              onClick={() => !isAlreadyImported && handleTeamPreview(division)}
                              sx={{
                                cursor: isAlreadyImported ? 'not-allowed' : 'pointer',
                                px: 2,
                                py: 1.5,
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <Typography
                                  variant="body1"
                                  noWrap
                                  sx={{
                                    flex: 1,
                                    color: isAlreadyImported ? 'text.disabled' : 'text.primary',
                                  }}
                                >
                                  {division.name}
                                </Typography>
                                {isAlreadyImported && (
                                  <Chip
                                    label="Already Added"
                                    size="small"
                                    color="default"
                                    sx={{
                                      height: 20,
                                      fontSize: '0.7rem',
                                      '& .MuiChip-label': { px: 1 },
                                    }}
                                  />
                                )}
                              </Box>
                              {/* Show matching team names as chips */}
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                {matchingTeams.map((team, idx) => renderTeamChip(team, idx, 'outlined', division, matchingTeams))}
                              </Box>
                            </Box>

                            {/* Team Preview Collapse */}
                            {!isAlreadyImported && (
                              <Collapse
                                in={selectedDivision?.id === division.id && showTeamPreview}
                                timeout="auto"
                              >
                                <Box sx={{ pl: 4, pr: 2, pb: 2 }}>
                                  {loadingTeams ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <CircularProgress size={16} />
                                      <Typography variant="body2" color="text.secondary">
                                        Loading teams...
                                      </Typography>
                                    </Box>
                                  ) : teamPreview.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                      No teams found
                                    </Typography>
                                  ) : (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                      {teamPreview.map((team, idx) => renderTeamChip(team, idx, 'outlined', division, teamPreview))}
                                    </Box>
                                  )}
                                </Box>
                              </Collapse>
                            )}
                          </Box>
                        );
                      })}
                    </List>
                  )}
                  </>
                ) : !teamSearchEnabled && (
                  /* Normal division list (no team search active) */
                  <>
                    {showDivisionSkeleton ? (
                      <List sx={{ '& .MuiSkeleton-root': { animationDelay: '0s !important' } }}>
                        {[1, 2, 3, 4, 5].map((i) => (
                          <Box key={i} sx={{ px: 2, py: 1.5, mb: 0.5 }}>
                            <Skeleton animation="wave" variant="text" width="60%" height={24} sx={{ mb: 0.5 }} />
                            <Skeleton animation="wave" variant="text" width={50} height={20} />
                          </Box>
                        ))}
                      </List>
                    ) : loadingDivisions ? (
                      null
                    ) : filteredDivisions.length === 0 ? (
                      <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                        No divisions found matching your filters.
                      </Typography>
                    ) : filteredDivisions.length > 50 ? (
                      <Box sx={{ textAlign: 'center', py: 4, px: 2 }}>
                        <Typography color="text.secondary" gutterBottom>
                          {filteredDivisions.length} divisions available
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Search for your team above, or use the filters to narrow down.
                        </Typography>
                      </Box>
                    ) : (
                      <List>
                        {filteredDivisions.map((division) => {
                          const isAlreadyImported = importedDivisionIds.has(division.id);
                          return (
                            <Box
                              key={division.id}
                              onClick={() => !isAlreadyImported && handleTeamPreview(division)}
                              sx={{
                                borderRadius: 1,
                                mb: 0.5,
                                cursor: isAlreadyImported ? 'not-allowed' : 'pointer',
                                bgcolor: selectedDivision?.id === division.id ? 'primary.50' : 'transparent',
                                border: 2,
                                borderColor: selectedDivision?.id === division.id ? 'primary.main' : 'transparent',
                                opacity: isAlreadyImported ? 0.6 : 1,
                                '@media (hover: hover)': {
                                  '&:hover': {
                                    bgcolor: isAlreadyImported
                                      ? 'transparent'
                                      : selectedDivision?.id === division.id
                                        ? 'primary.50'
                                        : 'action.hover'
                                  },
                                },
                              }}
                            >
                              <Box
                                sx={{
                                  px: 2,
                                  py: 1.5,
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                  <Typography
                                    variant="body1"
                                    noWrap
                                    sx={{
                                      flex: 1,
                                      color: isAlreadyImported ? 'text.disabled' : 'text.primary',
                                    }}
                                  >
                                    {division.name}
                                  </Typography>
                                  {division.flight && (
                                    <Chip
                                      label={division.flight}
                                      size="small"
                                      color="info"
                                      variant="outlined"
                                      sx={{
                                        height: 20,
                                        fontSize: '0.7rem',
                                        '& .MuiChip-label': { px: 1 },
                                      }}
                                    />
                                  )}
                                  {isAlreadyImported && (
                                    <Chip
                                      label="Already Added"
                                      size="small"
                                      color="default"
                                      sx={{
                                        height: 20,
                                        fontSize: '0.7rem',
                                        '& .MuiChip-label': { px: 1 },
                                      }}
                                    />
                                  )}
                                </Box>
                                {!isAlreadyImported && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                                    <Typography variant="body2" sx={{ mr: 0.5 }}>
                                      Teams
                                    </Typography>
                                    {selectedDivision?.id === division.id && showTeamPreview ? (
                                      <ExpandLessIcon fontSize="small" />
                                    ) : (
                                      <ExpandMoreIcon fontSize="small" />
                                    )}
                                  </Box>
                                )}
                              </Box>

                              {/* Team Preview Collapse */}
                              {!isAlreadyImported && (
                                <Collapse
                                  in={selectedDivision?.id === division.id && showTeamPreview}
                                  timeout="auto"
                                >
                                  <Box sx={{ pl: 4, pr: 2, pb: 2 }}>
                                    {loadingTeams ? (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <CircularProgress size={16} />
                                        <Typography variant="body2" color="text.secondary">
                                          Loading teams...
                                        </Typography>
                                      </Box>
                                    ) : teamPreview.length === 0 ? (
                                      <Typography variant="body2" color="text.secondary">
                                        No teams found
                                      </Typography>
                                    ) : (
                                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {teamPreview.map((team, idx) => renderTeamChip(team, idx, 'outlined', division, teamPreview))}
                                      </Box>
                                    )}
                                  </Box>
                                </Collapse>
                              )}
                            </Box>
                          );
                        })}
                      </List>
                    )}
                  </>
                )}

              </Box>
            )}

            {/* View 3: Custom League Form */}
            {currentView === 'custom' && (
              <CustomLeagueFormContent
                formData={customFormData}
                fieldErrors={customFieldErrors}
                onInputChange={handleCustomInputChange}
                detailsReadOnly={false}
                readOnly={false}
                editingLeague={null}
                fromCatalog={false}
                discoveryPlatform={customDiscoveryPlatform}
                syncTime={customSyncTime}
                onSyncTimeChange={setCustomSyncTime}
                user={user}
                orgSettings={orgSettings}
                getSyncSettingsSummary={() => customFormData.autoSyncEnabled ? '(Daily, auto-assigned)' : '(Disabled)'}
              />
            )}
          </Box>
        </Box>

        {/* Player picker - pinned above action buttons, inside DialogContent flex column */}
        {showPlayerPicker && (
          <Box sx={{ flexShrink: 0, px: 3, pb: 2, borderTop: 1, borderColor: 'divider' }}>
            <FormControl fullWidth size="small" sx={{ mt: 1 }}>
              <InputLabel>Assign Player</InputLabel>
              <Select
                value={selectedPlayerIdLocal || ''}
                label="Assign Player"
                onChange={(e) => setSelectedPlayerIdLocal(e.target.value || null)}
              >
                <MenuItem value="">
                  <em>None (assign later)</em>
                </MenuItem>
                {players!.map((p) => (
                  <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                Select a player to assign to {selectedTeamName} (optional)
              </Typography>
            </FormControl>
          </Box>
        )}

        {/* Info text when player is pre-known (PlayerTeamsTab flow) */}
        {teamPickerMode && selectedTeamName && selectedPlayerId && (
          <Box sx={{ flexShrink: 0, borderTop: 1, borderColor: 'divider', px: 3, py: 1.5 }}>
            <Alert severity="info" sx={{ py: 0 }}>
              Assigning <strong>{selectedPlayerName?.split(' ')[0] || 'Player'}</strong> to <strong>{selectedTeamName}</strong>
            </Alert>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: 1, borderColor: 'divider' }}>
        {/* View 1: Back button only if onBack prop exists */}
        {currentView === 'catalog' && onBack && (
          <Button variant="outlined" onClick={onBack} startIcon={<ArrowBackIcon />} sx={{ mr: 'auto' }}>
            Back
          </Button>
        )}
        {/* View 2: Back button */}
        {currentView === 'division' && (
          <Button variant="outlined" onClick={handleBackToCatalog} startIcon={<ArrowBackIcon />} sx={{ mr: 'auto' }}>
            Back
          </Button>
        )}
        {/* View 3: Back button */}
        {currentView === 'custom' && (
          <Button variant="outlined" onClick={handleBackFromCustom} startIcon={<ArrowBackIcon />} sx={{ mr: 'auto' }}>
            Back
          </Button>
        )}
        <Button variant="outlined" onClick={onClose} disabled={importing || customSubmitting} startIcon={<CancelIcon />}>
          Cancel
        </Button>
        {/* View 2: Action button */}
        {currentView === 'division' && (
          <Button
            variant="contained"
            onClick={onSelectDivision ? handleSelectDivision : handleImport}
            disabled={!selectedDivision || (isAssignMode && !selectedTeamName) || importing || loadingTeams}
            startIcon={getActionButtonIcon()}
          >
            {getActionButtonText()}
          </Button>
        )}
        {/* View 3: Add League button */}
        {currentView === 'custom' && (
          <Button
            variant="contained"
            onClick={handleCustomSubmit}
            disabled={!customFormValid || customSubmitting}
            startIcon={customSubmitting ? <CircularProgress size={20} /> : (customDiscoveryPlatform ? <ArrowForwardIcon /> : <AddIcon />)}
          >
            {customDiscoveryPlatform ? 'Next' : 'Add'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default LeagueCatalogDialog;
